// ============================================================================
// Mode picker popover — session mode selection
// ============================================================================

import { modeBadge, modeMenu, modeMenuList, modeOverlay } from "./dom.js";

let availableModesRef = () => [];
let currentModeIdRef = () => null;
let turboEnabledRef = () => false;
let sessionActiveRef = () => false;
let sendRef = null;

export function renderModeMenu() {
  modeMenuList.innerHTML = "";
  const modes = availableModesRef();
  const currentId = currentModeIdRef();
  if (modes.length === 0) {
    const empty = document.createElement("div");
    empty.className = "mode-menu-empty";
    empty.textContent = "No modes available";
    modeMenuList.appendChild(empty);
  } else {
    for (const m of modes) {
      const item = document.createElement("div");
      item.className = "mode-menu-item" + (m.id === currentId ? " selected" : "");
      item.dataset.modeId = m.id;
      const name = document.createElement("div");
      name.className = "mmi-name";
      name.textContent = m.name || m.id;
      if (m.id === currentId) {
        const check = document.createElement("span");
        check.className = "mmi-check";
        check.textContent = "✓";
        name.appendChild(check);
      }
      const desc = document.createElement("div");
      desc.className = "mmi-desc";
      desc.textContent = m.description || "";
      item.appendChild(name);
      if (m.description) item.appendChild(desc);
      item.addEventListener("click", (e) => {
        e.stopPropagation();
        haptic("tap");
        if (m.id !== currentId) sendRef({ type: "set_mode", modeId: m.id });
        closeModeMenu();
      });
      modeMenuList.appendChild(item);
    }
  }

  // Turbo is a gateway-level toggle, NOT an ACP mode — it sends set_turbo
  // (never set_mode with a synthetic id, which the agent would reject).
  // Rendered as a divider-separated row so it's reachable even when the
  // agent advertises no modes.
  const turboOn = turboEnabledRef();
  const divider = document.createElement("div");
  divider.className = "mode-menu-divider";
  modeMenuList.appendChild(divider);
  const turboItem = document.createElement("div");
  turboItem.className = "mode-menu-item mode-menu-turbo" + (turboOn ? " selected" : "");
  const tName = document.createElement("div");
  tName.className = "mmi-name";
  tName.textContent = "⚡ Turbo";
  if (turboOn) {
    const check = document.createElement("span");
    check.className = "mmi-check";
    check.textContent = "✓";
    tName.appendChild(check);
  }
  const tDesc = document.createElement("div");
  tDesc.className = "mmi-desc";
  tDesc.textContent = "Bypass permissions + auto-continue until the request is done";
  turboItem.appendChild(tName);
  turboItem.appendChild(tDesc);
  turboItem.addEventListener("click", (e) => {
    e.stopPropagation();
    haptic("tap");
    sendRef({ type: "set_turbo", enabled: !turboOn });
    closeModeMenu();
  });
  modeMenuList.appendChild(turboItem);
}

export function openModeMenu() {
  // Openable even with no advertised modes — the turbo row is always there.
  if (!sessionActiveRef()) return;
  renderModeMenu();
  modeMenu.classList.remove("hidden");
  const isMobile = window.matchMedia("(max-width: 600px)").matches;
  if (isMobile) {
    modeOverlay.classList.remove("hidden");
    modeMenu.style.left = "";
    modeMenu.style.top = "";
  } else {
    modeOverlay.classList.add("hidden");
    const rect = modeBadge.getBoundingClientRect();
    const menuW = Math.min(280, window.innerWidth - 16);
    let left = rect.left;
    if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;
    if (left < 8) left = 8;
    modeMenu.style.left = left + "px";
    modeMenu.style.top = (rect.bottom + 4) + "px";
  }
}

export function closeModeMenu() {
  modeMenu.classList.add("hidden");
  modeOverlay.classList.add("hidden");
}

export function toggleModeMenu() {
  if (modeMenu.classList.contains("hidden")) openModeMenu();
  else closeModeMenu();
}

export function isModeMenuOpen() {
  return !modeMenu.classList.contains("hidden");
}

/**
 * Initialize the mode picker.
 * @param {object} deps
 */
export function initModePicker(deps) {
  availableModesRef = deps.availableModes;
  currentModeIdRef = deps.currentModeId;
  turboEnabledRef = deps.turboEnabled || (() => false);
  sessionActiveRef = deps.sessionActive;
  sendRef = deps.send;

  modeBadge.addEventListener("click", (e) => { e.stopPropagation(); haptic("tap"); toggleModeMenu(); });
  modeBadge.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); haptic("tap"); toggleModeMenu(); }
  });
  modeMenu.addEventListener("click", (e) => e.stopPropagation());
  modeOverlay.addEventListener("click", () => { haptic("tap"); closeModeMenu(); });
}
